import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { TrendUp, Receipt, WarningCircle, Package, Wrench, Money, Users, ClipboardText, ArrowsCounterClockwise } from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#2563EB', '#F97316', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [extended, setExtended] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      Promise.all([fetchStats(), fetchCharts(), fetchExtended(), fetchMonthly()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) { console.error('Error fetching stats:', error); }
  };

  const fetchCharts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/charts`, { withCredentials: true });
      setCharts(response.data);
    } catch (error) { console.error('Error fetching charts:', error); }
  };

  const fetchExtended = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/extended`, { withCredentials: true });
      setExtended(response.data);
    } catch (error) { console.error('Error fetching extended:', error); }
  };

  const fetchMonthly = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/monthly-comparison`, { withCredentials: true });
      setMonthly(response.data);
    } catch (error) { console.error('Error fetching monthly:', error); }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="dashboard">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Tableau de bord</h1>
        <p className="text-slate-600 mb-8">Bienvenue, {user?.full_name || user?.username}</p>

        {user?.role === 'admin' && !loading && stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="stat-card" data-testid="stat-total-sales">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Ventes totales</span>
                  <TrendUp size={20} className="text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.total_sales.toFixed(2)} €</p>
              </div>

              <div className="stat-card" data-testid="stat-total-invoices">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Factures</span>
                  <Receipt size={20} className="text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.total_invoices}</p>
              </div>

              <div className="stat-card" data-testid="stat-low-stock">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Stock bas</span>
                  <WarningCircle size={20} className="text-orange-500" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.low_stock_items}</p>
              </div>
            </div>

            {/* Extended KPIs */}
            {extended && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4" data-testid="kpi-services-revenue">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-blue-700">CA Prestations</span>
                    <Wrench size={18} className="text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{extended.services_revenue.toFixed(2)} €</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4" data-testid="kpi-products-revenue">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-orange-700">CA Produits</span>
                    <Package size={18} className="text-orange-600" />
                  </div>
                  <p className="text-2xl font-bold text-orange-900">{extended.products_revenue.toFixed(2)} €</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-clients">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Clients</span>
                    <Users size={18} className="text-slate-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{extended.total_clients}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-pending-quotes">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Devis en attente</span>
                    <ClipboardText size={18} className="text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{extended.pending_quotes}</p>
                </div>
                <div className={`border rounded-lg p-4 ${extended.cash_session_open ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' : 'bg-slate-50 border-slate-200'}`} data-testid="kpi-cash-balance">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-700">Solde caisse</span>
                    <Money size={18} className={extended.cash_session_open ? 'text-green-600' : 'text-slate-400'} />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {extended.cash_session_open ? `${extended.current_cash_balance.toFixed(2)} €` : 'Fermée'}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-credit-notes">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Avoirs émis</span>
                    <ArrowsCounterClockwise size={18} className="text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-600">
                    -{extended.total_credit_notes_amount.toFixed(2)} €
                  </p>
                  <p className="text-xs text-slate-500">{extended.total_credit_notes_count} avoir(s)</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-services-catalog">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Catalogue prestations</span>
                    <Wrench size={18} className="text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{extended.total_services_count}</p>
                </div>
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 rounded-lg p-4" data-testid="kpi-net-revenue">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-slate-700">CA Net</span>
                    <TrendUp size={18} className="text-slate-700" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {(stats.total_sales - extended.total_credit_notes_amount).toFixed(2)} €
                  </p>
                </div>
              </div>
            )}

            {/* Monthly comparison chart */}
            {monthly && monthly.monthly.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="chart-monthly-comparison">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Évolution mensuelle : Prestations vs Produits</h2>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Comparaison du CA par activité sur les 12 derniers mois</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthly.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value) => [`${Number(value).toFixed(2)} €`, '']}
                    />
                    <Legend />
                    <Bar dataKey="services" name="Prestations" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="products" name="Produits" fill="#F97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Charts Section */}
            {charts && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Daily Sales Chart */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="chart-daily-sales">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">Ventes des 30 derniers jours</h2>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Évolution du chiffre d'affaires</p>
                  {charts.daily_sales.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={charts.daily_sales} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          tickFormatter={(date) => {
                            const d = new Date(date);
                            return `${d.getDate()}/${d.getMonth() + 1}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                          formatter={(value) => [`${value.toFixed(2)} €`, 'Total']}
                        />
                        <Line type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-slate-500 text-sm">Aucune donnée disponible</div>
                  )}
                </div>

                {/* Top Products Chart */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="chart-top-products">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">Top 5 produits vendus</h2>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Par quantité</p>
                  {charts.top_products.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={charts.top_products} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10, fill: '#64748B' }}
                          interval={0}
                          angle={-15}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                        />
                        <Bar dataKey="quantity" fill="#F97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-slate-500 text-sm">Aucune donnée disponible</div>
                  )}
                </div>

                {/* Sales by Type */}
                <div className="bg-white border border-slate-200 rounded-lg p-6 lg:col-span-2" data-testid="chart-sales-by-type">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">Répartition des ventes par type</h2>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Chiffre d'affaires par catégorie de service</p>
                  {charts.sales_by_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={charts.sales_by_type}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ type, total }) => `${type}: ${total.toFixed(2)} €`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="total"
                          nameKey="type"
                        >
                          {charts.sales_by_type.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                          formatter={(value) => [`${value.toFixed(2)} €`, 'Total']}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-slate-500 text-sm">Aucune donnée disponible</div>
                  )}
                </div>
              </div>
            )}

            {/* Recent Invoices */}
            {stats.recent_invoices.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Factures récentes</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Numéro</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_invoices.map((invoice) => (
                        <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-900">{invoice.invoice_number}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{invoice.client_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{invoice.total_amount.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {user?.role === 'seller' && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Bienvenue sur LBC</h2>
            <p className="text-slate-600">
              Utilisez le menu de navigation pour gérer les produits, clients et factures.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
